import { fromFile } from './../bundled-dependencies/id3js';
import type { AudioComponentData, ImageComponentData } from './../ricos-content';
import type { PluginServices } from './../ricos-plugins/editorPlugins';
import type { PublicCommands } from './../ricos-types';
import { logInvariant } from './../_shared/log-invariant';
import type { PickMediaFn } from './../_shared/media/pick-media';
import type { PmAudioNode } from './../_shared/pm-node-types';
import { upload } from './../_shared/upload/upload';
import { audioFileTypes } from './/consts';
import { RICOS_DEFAULTS } from './/defaults';

import type { AudioPluginEditorConfig } from './types';
import { AUDIO_TYPE } from './types';

export const getAudioPickMedia = ({
  services,
  config,
  type,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: AudioPluginEditorConfig;
  type: 'audio' | 'image';
}): PickMediaFn => {
  const { handleFileUpload, handleFileSelection } = config;
  const devPickMedia = services.devService().pickMedia;

  const pickMedia: PickMediaFn = devPickMedia
    ? devPickMedia
    : handleFileSelection
    ? async () => {
        const data = await new Promise<AudioComponentData | ImageComponentData>(resolve =>
          handleFileSelection(({ data }) => resolve(data), undefined, type === 'image')
        );
        return { type: 'ready-media' as const, media: [data] };
      }
    : async ({ mimeType }: { mimeType: string }) => {
        const files =
          (await services.uploadService?.selectFiles({ accept: mimeType, multiple: false })) ?? [];
        return { type: 'file' as const, file: files, customUploadFn: handleFileUpload };
      };

  return pickMedia;
};

type GetAudioAttributesProps =
  | {
      type: 'audio';
      componentData: PmAudioNode['attrs'];
      uploadData: PmAudioNode['attrs'];
    }
  | {
      type: 'image';
      componentData: PmAudioNode['attrs'];
      uploadData: { file_name: string; width: number; height: number };
    };

const getAudioAttributes = ({ type, uploadData, componentData }: GetAudioAttributesProps) => {
  if (type === 'audio') {
    const name = uploadData?.name ?? componentData?.name ?? '';
    const authorName = uploadData?.authorName ?? componentData?.authorName ?? '';
    const audio = uploadData?.audio ?? componentData?.audio ?? null;

    return {
      ...componentData,
      audio,
      name,
      authorName,
      html: null,
      tempData: undefined,
      loading: undefined,
    } satisfies PmAudioNode['attrs'];
  }

  return {
    ...componentData,
    coverImage: {
      src: { id: uploadData.file_name },
      width: uploadData.width,
      height: uploadData.height,
    },
    tempData: undefined,
    loading: undefined,
  } satisfies PmAudioNode['attrs'];
};

export const handleAudioUpload = async ({
  services,
  config,
  publicCommands,
  nodeId,
  type,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: AudioPluginEditorConfig;
  publicCommands: PublicCommands;
  nodeId?: string;
  type: 'audio' | 'image';
}) => {
  const mimeType = type === 'audio' ? audioFileTypes : 'image/*';
  const pickMedia = getAudioPickMedia({ services, config, type });

  const result = await pickMedia({ mimeType, multiple: false });

  switch (result.type) {
    case 'ready-media': {
      const data = result.media[0] as AudioComponentData;
      const id = nodeId || publicCommands.insertBlockWithBlankLines(AUDIO_TYPE, RICOS_DEFAULTS);
      setTimeout(() => {
        const componentData: PmAudioNode['attrs'] = publicCommands?.getBlockComponentData(id);
        const updatedData = getAudioAttributes({
          type,
          componentData,
          uploadData: data as any,
        });

        publicCommands?.setBlock(id, AUDIO_TYPE, {
          ...updatedData,
          tempData: undefined,
          loading: undefined,
          error: undefined,
        });
      });
      return;
    }
    case 'file': {
      const file = result.file[0];
      if (file) {
        const tags = await getAudioTags(file);
        const targetNodeId =
          nodeId ||
          publicCommands.insertBlockWithBlankLines(AUDIO_TYPE, {
            ...RICOS_DEFAULTS,
            ...tags,
          });

        const trackBi = services.uploadService?.trackUploadBi(AUDIO_TYPE, file.size, file.name);

        try {
          const url = URL.createObjectURL(file);
          await Promise.resolve();
          const componentData = publicCommands?.getBlockComponentData(targetNodeId);

          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, {
            ...componentData,
            name: componentData.name || file.name.split('.')[0],
            tempData: url,
            loading: true,
            error: null,
          });

          trackBi?.start();
          const uploadFn = result.customUploadFn || config.handleFileUpload;

          if (!uploadFn) {
            logInvariant(Error('No upload function provided'));
            throw new Error('No upload function provided');
          }

          const uploadedFile = await upload(uploadFn, file);

          const updatedData = getAudioAttributes({
            type,
            componentData: publicCommands?.getBlockComponentData(targetNodeId),
            uploadData: uploadedFile.data as any,
          });

          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, updatedData);

          URL.revokeObjectURL(url as string);
          trackBi?.success();
        } catch (error: any) {
          const componentData = publicCommands?.getBlockComponentData(targetNodeId);
          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, {
            ...componentData,
            error,
            loading: null,
          });

          trackBi?.error(error);
          services.uploadService?.notifyError(error);
        }
      } else {
        console.error('No file selected');
      }
      return;
    }
    case 'cancel': {
      return;
    }
    default: {
      console.error('Unhandeled media picker result:', result);
      return;
    }
  }
};

const getAudioTags = async (file: File) => {
  try {
    const tags = await fromFile(file);
    return { name: tags?.title, authorName: tags?.artist };
  } catch (error) {
    console.error(error);
    return {};
  }
};
